import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Eye, ArrowLeft, ArrowRight, ChevronRight, Headphones, Mic, BookOpen, PenTool, X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODALITY_ICONS = { listening: Headphones, speaking: Mic, reading: BookOpen, writing: PenTool };
const MODALITY_COLORS = {
  listening: 'bg-blue-50 border-blue-200 text-blue-800',
  speaking: 'bg-green-50 border-green-200 text-green-800',
  reading: 'bg-purple-50 border-purple-200 text-purple-800',
  writing: 'bg-orange-50 border-orange-200 text-orange-800',
};

const ExamPreview = () => {
  const { gradeLevel } = useParams();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIdx, setCurrentIdx] = useState(0);

  useEffect(() => {
    const fetchQuestions = async () => {
      try {
        const res = await axios.get(`${API}/exam-questions?grade_level=${gradeLevel}&form_id=A`);
        setQuestions(res.data.questions || []);
      } catch {
        toast.error('Failed to load preview');
      } finally {
        setLoading(false);
      }
    };
    fetchQuestions();
  }, [gradeLevel]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const q = questions[currentIdx];
  const totalByModality = {};
  questions.forEach(q => {
    totalByModality[q.modality] = (totalByModality[q.modality] || 0) + 1;
  });

  return (
    <div className="max-w-4xl mx-auto px-6 py-8" data-testid="exam-preview">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Link to={`/exam/setup/${gradeLevel}`}>
            <Button variant="ghost" size="sm" className="h-8 px-2"><ArrowLeft size={18} /></Button>
          </Link>
          <div>
            <div className="flex items-center gap-2">
              <Eye size={18} className="text-[#002FA7]" />
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                Preview Mode — Grade {gradeLevel}
              </h1>
            </div>
            <p className="text-sm text-gray-500">Read-only preview. No data is saved.</p>
          </div>
        </div>
        <div className="bg-amber-50 border border-amber-200 px-3 py-1.5 text-amber-800 text-xs font-semibold">
          PREVIEW ONLY
        </div>
      </div>

      {/* Modality Summary */}
      <div className="flex gap-2 mb-6">
        {Object.entries(totalByModality).map(([mod, count]) => {
          const Icon = MODALITY_ICONS[mod] || BookOpen;
          return (
            <div key={mod} className={`flex items-center gap-2 px-3 py-2 border ${MODALITY_COLORS[mod] || 'bg-gray-50 border-gray-200'}`}>
              <Icon size={14} />
              <span className="text-xs font-semibold capitalize">{mod}</span>
              <span className="text-xs opacity-70">{count} items</span>
            </div>
          );
        })}
        <div className="ml-auto text-sm text-gray-500 self-center">
          {questions.length} total questions
        </div>
      </div>

      {/* Question Display */}
      {q ? (
        <div className="border-2 border-gray-200 mb-6">
          {/* Question header */}
          <div className="bg-gray-50 p-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-900 text-white flex items-center justify-center text-sm font-bold">
                {currentIdx + 1}
              </div>
              <div>
                <span className={`text-xs font-semibold px-2 py-0.5 border capitalize ${MODALITY_COLORS[q.modality] || ''}`}>
                  {q.modality}
                </span>
                {q.wida_level && (
                  <span className="text-xs text-gray-400 ml-2">Proficiency Level {q.wida_level}</span>
                )}
              </div>
            </div>
            <span className="text-xs text-gray-400">
              {currentIdx + 1} of {questions.length}
            </span>
          </div>

          {/* Question content */}
          <div className="p-6">
            {q.stimulus && (
              <div className="mb-4 p-4 bg-blue-50 border border-blue-100 text-sm text-gray-700 italic leading-relaxed">
                {q.stimulus}
              </div>
            )}
            <h3 className="text-base font-semibold mb-4">{q.prompt || q.question_text}</h3>

            {/* Multiple choice options */}
            {q.options && q.options.length > 0 && (
              <div className="space-y-2">
                {q.options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 border border-gray-200 text-sm hover:bg-gray-50">
                    <div className="w-6 h-6 border-2 border-gray-300 flex items-center justify-center text-xs font-bold text-gray-400 flex-shrink-0">
                      {String.fromCharCode(65 + i)}
                    </div>
                    <span>{typeof opt === 'string' ? opt : opt.text}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Open response area */}
            {(q.modality === 'speaking' || q.modality === 'writing') && (!q.options || q.options.length === 0) && (
              <div className="p-4 border-2 border-dashed border-gray-300 text-sm text-gray-400 text-center">
                {q.modality === 'speaking' ? 'Student records audio response here' : 'Student writes response here'}
              </div>
            )}

            {/* Scoring rubric hint */}
            {q.rubric && (
              <div className="mt-4 p-3 bg-gray-50 border border-gray-200 text-xs text-gray-500">
                <span className="font-semibold">Rubric:</span> {q.rubric}
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="border-2 border-dashed border-gray-200 p-12 text-center">
          <p className="text-gray-400">No questions found for this grade level.</p>
        </div>
      )}

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => setCurrentIdx(Math.max(0, currentIdx - 1))}
          disabled={currentIdx === 0}
          className="btn-sharp h-10"
          data-testid="preview-prev"
        >
          <ArrowLeft size={16} className="mr-2" /> Previous
        </Button>
        <span className="text-sm text-gray-500">{currentIdx + 1} / {questions.length}</span>
        <Button
          variant="outline"
          onClick={() => setCurrentIdx(Math.min(questions.length - 1, currentIdx + 1))}
          disabled={currentIdx >= questions.length - 1}
          className="btn-sharp h-10"
          data-testid="preview-next"
        >
          Next <ArrowRight size={16} className="ml-2" />
        </Button>
      </div>
    </div>
  );
};

export default ExamPreview;
